import { useMemo } from 'react';
import * as yup from 'yup';

import { DomesticContentAttestmentType, ProgramType } from 'types';

import { useGetPVSystemSchema } from '../Account/PVSystem';
import { addersSchema } from '../Adders/AddersTab';
import { permitDateFields } from '../MilestonePackagePartials/permitDateFields';

export const useInstallPackageSchema = ({
  isDomesticContentEligible,
  attestmentType,
  installerRequired,
  accountRequiresSolarModuleSerialNum,
  programType,
}: {
  isDomesticContentEligible: boolean;
  attestmentType: DomesticContentAttestmentType;
  installerRequired: boolean;
  isFinCoUser: boolean;
  accountRequiresSolarModuleSerialNum: boolean;
  programType?: ProgramType;
}) => {
  const isNewCommunity: boolean = programType === ProgramType.newCommunity;
  const isNewHomes: boolean = programType === ProgramType.newHomes;
  const pvSystemSchema = useGetPVSystemSchema();
  const inverterSchema = yup.object({
    model: yup.string().required('Inverter Model is a required field.'),
    count: yup
      .number()
      .required('Inverter Count is a required field.')
      .min(1, 'Inverter Count must be greater than 0.'),
  });
  //We don't need the arrays package on the install package submission
  delete (pvSystemSchema.fields as any)['arrays'];

  const pvSystemInstallPackageSchema = yup.object({ ...pvSystemSchema.fields }).concat(
    yup.object({
      panelManufacturer: yup.string().required('Panel Manufacturer is a required field.'),
      panelModel: yup.string().required('Panel Model is a required field.'),
      totalPanelCount: yup
        .number()
        .required('Panel Count is a required field.')
        .min(1, 'Total Panel Count must be greater than 0.'),
      inverterManufacturer: yup.string().required('Inverter Manufacturer is a required field.'),
      inverterModels: yup
        .array()
        .of(inverterSchema)
        .required('Inverter Models are required.')
        .min(1, 'Inverter Models must have at least one entry.'),
      mountingType:
        programType === ProgramType.doePr
          ? yup.string().optional()
          : yup.string().required('Mounting Type is a required field.'),
      mountingHardware:
        isDomesticContentEligible && attestmentType !== DomesticContentAttestmentType.storageOnly
          ? yup
              .array()
              .required('Mounting Hardware is a required field.')
              .min(1, 'Mounting Hardware Models must have at least one entry.')
          : yup.array(),
      mountingManufacturer:
        (isDomesticContentEligible && attestmentType !== DomesticContentAttestmentType.storageOnly) ||
        programType === ProgramType.doePr
          ? yup.string().optional()
          : yup.string().required('Mounting Manufacturer is a required field.'),
    }),
  );

  const permitSchema = yup.object({
    ...permitDateFields,
    scheduledInspectionDate: yup.string().when([], {
      is: () => !isNewCommunity,
      then: (schema) =>
        schema
          .required('Scheduled Inspection Date is a required field.')
          .matches(/^\d{2}-\d{2}-\d{4}$/, 'Scheduled Inspection Date must be in MM-DD-YYYY format.'),
      otherwise: (schema) => schema.notRequired(),
    }),
    expectedPTODate: yup.string().when([], {
      is: () => !isNewCommunity,
      then: (schema) =>
        schema
          .required('Expected PTO Date is a required field.')
          .matches(/^\d{2}-\d{2}-\d{4}$/, 'Expected PTO Date must be in MM-DD-YYYY format.'),
      otherwise: (schema) => schema.notRequired(),
    }),
    executedEscrowAgreementDocumentDate: yup.string().when([], {
      is: () => isNewHomes,
      then: (schema) =>
        schema
          .required('Executed Escrow Agreement Document Date is a required field.')
          .matches(/^\d{2}-\d{2}-\d{4}$/, 'Executed Escrow Agreement Document Date must be in MM-DD-YYYY format.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const baseInstallPackageSchema = permitSchema.concat(addersSchema).concat(pvSystemInstallPackageSchema);
  const installerSchema = yup.object().shape({
    legalName: yup.string().required('Legal name is a required field.'),
    iccDocketNumber: yup.string().required('ICC Docket Number is a required field.'),
    qualifiedPersonOnsiteFirstName: yup.string().required('Qualified Person Onsite First Name is a required field.'),
    qualifiedPersonOnsiteLastName: yup.string().required('Qualified Person Onsite Last Name is a required field.'),
    phoneNumber: yup.string().required('Phone Number is a required field.'),
    email: yup.string().required('Email is a required field.').email('Email is not a valid email.'),
    address: yup.string().required('Address is a required field.'),
    city: yup.string().required('City is a required field.'),
    installerState: yup.string().required('State is a required field.'),
  });

  const serialNumberSchema = yup.object({
    solarModuleSerialNumber: yup
      .string()
      .trim()
      .when([], {
        is: () => !isNewCommunity,
        then: (schema) =>
          schema
            .required('Serial Number is a required field.')
            .min(1, 'Serial Number cannot be empty or whitespace only.'),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  const installPackageSchema = useMemo(() => {
    let schemaToValidate = baseInstallPackageSchema;

    if (installerRequired) {
      schemaToValidate = schemaToValidate.shape(installerSchema.fields);
    }
    //TODO: keep only accountRequiresSolarModuleSerialNum shortly after post launch of FIN-2462
    if (isDomesticContentEligible || accountRequiresSolarModuleSerialNum) {
      schemaToValidate = schemaToValidate.shape(serialNumberSchema.fields);
    }
    return schemaToValidate;
  }, [
    baseInstallPackageSchema,
    installerRequired,
    isDomesticContentEligible,
    accountRequiresSolarModuleSerialNum,
    installerSchema.fields,
    serialNumberSchema.fields,
  ]);

  return installPackageSchema;
};
